/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useRef } from "react";
import {
  ScrollView,
  TextInput,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import { Editor } from "draft-js";
import Icon from "react-icons-kit";
import { exclamationCircle } from "react-icons-kit/fa/exclamationCircle";
import ReactTooltip from "react-tooltip";

import { Button, Text, styles } from "UpduoLibrary";
import {
  skaleBorderLightGray,
  skalePrimaryBlue,
  skaleRed,
  skaleCellRed,
  skaleWebTextLightGray,
  skaleWebTextGray,
  skaleWebTextBackgroundLightGray,
  skaleWhite,
  skaleYellow,
} from "config/UpduoColors";

import { useSkillStepCard } from "./useSkillStepCard";

export default function SkillStepCard({
  skillSessionStepRole,
  onEdit,
  cardWidth,
}) {
  const {
    _handleChangeHeading,
    _handleChangeDetailTitle,
    _handleChangeDetailContent,
    _handleToggleDetail,
    _handleDeleteDetail,
    _handleKeepButton,
    editorState,
    editorContentLength,
    showDeleteDetailPrompt,
  } = useSkillStepCard({
    skillSessionStepRole,
    onEdit,
  });

  const editor = useRef<any>();

  if (!skillSessionStepRole) {
    return null;
  }
  const detailEditor = (
    <View
      style={{
        marginTop: 32,
      }}
    >
      <Text style={{ marginBottom: 12, color: skaleWebTextGray, fontSize: 14 }}>
        {"Detail Title:"}
      </Text>
      <TextInput
        style={[
          styles.skillInput,
          {
            backgroundColor: skaleWebTextBackgroundLightGray,
            borderColor: skaleBorderLightGray,
            color: skaleWebTextLightGray,
          },
        ]}
        value={skillSessionStepRole.detailTitle}
        onChangeText={_handleChangeDetailTitle}
      />
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Text
          style={{
            marginTop: 24,
            marginBottom: 12,
            color: skaleWebTextGray,
            fontSize: 14,
          }}
        >
          {"Detail Content:"}
        </Text>
        <Text
          style={{
            marginTop: 24,
            marginBottom: 12,
            color: skaleWebTextGray,
            fontSize: 14,
          }}
        >
          {`${editorContentLength} characters`}
        </Text>
      </View>
      <TouchableOpacity
        onPress={() => {
          editor.current?.focus();
        }}
        activeOpacity={1}
      >
        <ScrollView
          style={[
            styles.descriptionInput,
            {
              backgroundColor: skaleWebTextBackgroundLightGray,
              borderColor: skaleBorderLightGray,
            },
            {
              color: skaleWebTextLightGray,
            } as ViewStyle,
          ]}
        >
          <Editor
            editorState={editorState}
            onChange={_handleChangeDetailContent}
            ref={editor}
          />
        </ScrollView>
      </TouchableOpacity>
    </View>
  );
  const deleteDetailPrompt = (
    <View style={{ flexDirection: "row", marginTop: 10, alignItems: "center" }}>
      <Text style={{ color: skaleWebTextGray }}>{"Delete detail?"}</Text>
      <Button
        text={"Keep"}
        width={80}
        marginLeft={10}
        onPress={_handleKeepButton}
        height={28}
        borderWidth={1}
        borderColor={skalePrimaryBlue}
        color={skaleWhite}
        textColor={skalePrimaryBlue}
      />
      <Button
        text={"Delete"}
        width={80}
        marginLeft={10}
        onPress={_handleDeleteDetail}
        height={28}
        borderWidth={1}
        borderColor={skaleRed}
        color={skaleWhite}
        textColor={skaleRed}
      />
    </View>
  );

  const recommendCharacterCount = (
    <View style={{ flex: 1, flexDirection: "row" }}>
      <span data-tip data-for={"recommendCharacterCount"}>
        <Text
          style={{
            marginBottom: 5,
            color: skaleYellow,
            fontSize: 14,
            fontWeight: "bold",
          }}
        >
          {` Recommend < 135 characters  `}
          <Icon size={15} icon={exclamationCircle} />
        </Text>
      </span>
      <ReactTooltip
        id={"recommendCharacterCount"}
        type={"dark"}
        effect={"solid"}
      >
        <Text style={{ color: skaleWhite, fontSize: 14 }}>
          {
            "Keep it simple and short, users  \ntake a lot longer to read when there are  \nmore than 135 characters."
          }
        </Text>
      </ReactTooltip>
    </View>
  );

  return (
    <View
      style={Object.assign(
        {
          minWidth: 430,
          borderWidth: 1,
          borderRadius: 12,
          borderColor: skaleBorderLightGray,
          backgroundColor: skaleWhite,
        },
        cardWidth ? { width: cardWidth } : { flexBasis: "50%" }
      )}
    >
      <View style={{ paddingVertical: 24, paddingHorizontal: 20 }}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Text
            style={{ marginBottom: 5, color: skaleWebTextGray, fontSize: 14 }}
          >
            {"Heading:"}
          </Text>
          {skillSessionStepRole.heading.length >= 135
            ? recommendCharacterCount
            : null}
          <Text
            style={{
              marginBottom: 5,
              color: skaleWebTextGray,
              fontSize: 14,
            }}
          >
            {`${skillSessionStepRole.heading.length} characters`}
          </Text>
        </View>
        <TextInput
          multiline={true}
          style={[
            styles.descriptionInput,
            {
              paddingVertical: 12,
              fontFamily: "ObjectSans-Regular",
              fontSize: 16,
              height: 75,
              backgroundColor: skaleWebTextBackgroundLightGray,
              borderColor: skaleBorderLightGray,
              color: skaleWebTextLightGray,
            },
            {
              outline: "none",
            } as TextStyle,
          ]}
          value={skillSessionStepRole.heading}
          onChangeText={_handleChangeHeading}
        />
        <View
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
          }}
        >
          <View
            style={{
              alignItems: "flex-end",
              marginTop: 5,
            }}
          >
            <Button
              text={
                skillSessionStepRole.detailContent
                  ? "Remove Detail"
                  : "Add Detail"
              }
              textColor={
                skillSessionStepRole.detailContent ? skaleRed : skaleWhite
              }
              width={230}
              onPress={_handleToggleDetail}
              height={42}
              color={
                skillSessionStepRole.detailContent
                  ? skaleCellRed
                  : skalePrimaryBlue
              }
            />
            {showDeleteDetailPrompt ? deleteDetailPrompt : null}
          </View>
        </View>
        {skillSessionStepRole.detailContent || skillSessionStepRole.detailTitle
          ? detailEditor
          : null}
      </View>
    </View>
  );
}
